"use client";

import { useState, useEffect } from "react";
import styles from "./Carousel.module.css";
import classNames from "classnames/bind";
import { CarouselButton } from "../CarouselButton/CarouselButton";
import { useVisibleCount } from "./useVisibleCount";

const cx = classNames.bind(styles);

export function Carousel({
  items,
  headline = "Our latest stories",
  trackClassName,
  description,
  showNav = true,
  aspectRatio = "4:3",
  seeAllLink,
  isPressRelease = false,
}: {
  items: React.ReactNode[];
  headline?: string;
  description?: string;
  showNav?: boolean;
  aspectRatio?: "4:3" | "16:9";
  trackClassName?: string;
  isPressRelease?: boolean;
  seeAllLink?: { url: string; label?: string };
}) {
  const withPeek = !description && items.length > 2;
  const visibleCount = useVisibleCount(items.length, withPeek);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index > items.length - Math.floor(visibleCount)) {
      setIndex(Math.max(items.length - Math.floor(visibleCount), 0));
    }
    // eslint-disable-next-line
  }, [visibleCount, items]);

  const canNav =
    showNav && !description && items.length > 2 && visibleCount > 1;

  function goToPrevious() {
    setIndex((prev) => Math.max(prev - 1, 0));
  }
  function goToNext() {
    setIndex((prev) =>
      Math.min(prev + 1, items.length - Math.floor(visibleCount)),
    );
  }

  const isTwoItemsDesktop = items.length === 2 && visibleCount > 1;

  // Map aspectRatio to semantic data-width
  const aspect = aspectRatio === "16:9" ? "wide" : "narrow";
  const layout = isTwoItemsDesktop ? "two-columns" : "multi-columns";

  // Variant logic for team and press-release
  const variant = isPressRelease
    ? "press-release"
    : trackClassName === "carousel-track--team"
      ? "team"
      : undefined;

  return (
    <section className={cx("container")}>
      <div className={cx("inner")}>
        <div
          className={cx(
            "carousel-header",
            isPressRelease && "carousel-header--press",
          )}
          data-variant={variant}
        >
          <h2 className={cx("carousel-headline", "text-h1")}>{headline}</h2>
          {description ? (
            <p className={cx("carousel-desc")}>{description}</p>
          ) : seeAllLink && seeAllLink.url ? (
            <a
              href={seeAllLink.url}
              className={cx("see-all-link")}
              tabIndex={0}
            >
              {seeAllLink.label || "See all"}
            </a>
          ) : canNav ? (
            <div className={cx("carousel-nav")}>
              <CarouselButton
                kind="previous"
                onClick={goToPrevious}
                disabled={index === 0}
                className={cx("nav-btn")}
              />
              <CarouselButton
                kind="next"
                onClick={goToNext}
                disabled={index >= items.length - Math.floor(visibleCount)}
                className={cx("nav-btn")}
              />
            </div>
          ) : null}
        </div>
        <div className={cx("carousel-track-outer")} data-layout={layout}>
          <div
            className={cx("carousel-track", trackClassName)}
            data-width={aspect}
            data-variant={variant}
            style={{
              transform:
                visibleCount > 1 && !isTwoItemsDesktop
                  ? `translateX(-${index * (100 / visibleCount)}%)`
                  : undefined,
            }}
          >
            {items.map((item, i) => (
              <div className={cx("carousel-item")} key={i}>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Carousel;
